import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

interface MFAFactor {
  id: string;
  type: 'totp';
  friendly_name?: string;
  status: 'verified' | 'unverified';
}

interface MFAState {
  isEnrolled: boolean;
  isVerified: boolean;
  factors: MFAFactor[];
  isLoading: boolean;
  error: string | null;
}

interface TOTPEnrollment {
  id: string;
  type: 'totp';
  totp: {
    qr_code: string;
    secret: string;
    uri: string;
  };
}

/** Manages TOTP-based multi-factor authentication: enroll, verify, unenroll, and session status. */
export const useMFA = () => {
  const { user } = useAuth();
  const [state, setState] = useState<MFAState>({
    isEnrolled: false,
    isVerified: false,
    factors: [],
    isLoading: true,
    error: null,
  });

  // Check MFA status on mount and when user changes
  const checkMFAStatus = useCallback(async () => {
    if (!user) {
      setState(prev => ({ ...prev, isLoading: false }));
      return;
    }

    try {
      setState(prev => ({ ...prev, isLoading: true, error: null }));

      // Get enrolled factors
      const { data: factorsData, error: factorsError } = await supabase.auth.mfa.listFactors();

      if (factorsError) {
        throw factorsError;
      }

      const verifiedFactors = factorsData?.totp?.filter(f => f.status === 'verified') || [];
      const allFactors = factorsData?.totp || [];

      // Check current assurance level
      const { data: aalData, error: aalError } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();

      if (aalError) {
        throw aalError;
      }

      const isEnrolled = verifiedFactors.length > 0;
      const isVerified = aalData?.currentLevel === 'aal2';

      setState({
        isEnrolled,
        isVerified,
        factors: allFactors.map(f => ({
          id: f.id,
          type: 'totp' as const,
          friendly_name: f.friendly_name || undefined,
          status: f.status as 'verified' | 'unverified',
        })),
        isLoading: false,
        error: null,
      });
    } catch (error: any) {
      console.error("MFA status check error:", error);
      setState(prev => ({
        ...prev,
        isLoading: false,
        error: error.message || "Failed to check MFA status",
      }));
    }
  }, [user]);

  useEffect(() => {
    checkMFAStatus();
  }, [checkMFAStatus]);

  // Start TOTP enrollment
  const enrollTOTP = async (friendlyName?: string): Promise<TOTPEnrollment | null> => {
    try {
      const { data, error } = await supabase.auth.mfa.enroll({
        factorType: 'totp',
        friendlyName: friendlyName || 'CastReady Admin TOTP',
      });

      if (error) {
        throw error;
      }

      return data as TOTPEnrollment;
    } catch (error: any) {
      console.error("TOTP enrollment error:", error);
      setState(prev => ({ ...prev, error: error.message }));
      return null;
    }
  };

  // Verify TOTP code during enrollment
  const verifyTOTP = async (factorId: string, code: string): Promise<boolean> => {
    try {
      const { data, error } = await supabase.auth.mfa.challengeAndVerify({
        factorId,
        code,
      });

      if (error) {
        throw error;
      }

      // Refresh MFA status after verification
      await checkMFAStatus();
      return true;
    } catch (error: any) {
      console.error("TOTP verification error:", error);
      setState(prev => ({ ...prev, error: error.message }));
      return false;
    }
  };

  // Verify MFA for session (when already enrolled)
  const verifyMFASession = async (code: string): Promise<boolean> => {
    try {
      if (state.factors.length === 0) {
        throw new Error("No MFA factors enrolled");
      }

      const verifiedFactor = state.factors.find(f => f.status === 'verified');
      if (!verifiedFactor) {
        throw new Error("No verified MFA factor found");
      }

      const { data, error } = await supabase.auth.mfa.challengeAndVerify({
        factorId: verifiedFactor.id,
        code,
      });

      if (error) {
        throw error;
      }

      // Refresh status
      await checkMFAStatus();
      return true;
    } catch (error: any) {
      console.error("MFA session verification error:", error);
      setState(prev => ({ ...prev, error: error.message }));
      return false;
    }
  };

  // Unenroll a factor
  const unenrollFactor = async (factorId: string): Promise<boolean> => {
    try {
      const { error } = await supabase.auth.mfa.unenroll({ factorId });

      if (error) {
        throw error;
      }

      await checkMFAStatus();
      return true;
    } catch (error: any) {
      console.error("MFA unenroll error:", error);
      setState(prev => ({ ...prev, error: error.message }));
      return false;
    }
  };

  // Clear error
  const clearError = () => {
    setState(prev => ({ ...prev, error: null }));
  };

  return {
    ...state,
    enrollTOTP,
    verifyTOTP,
    verifyMFASession,
    unenrollFactor,
    refreshStatus: checkMFAStatus,
    clearError,
  };
};

// Hook to check if MFA is required for current user
export const useMFARequired = () => {
  const { hasRole } = useAuth();
  const isAdmin = hasRole("admin");
  
  // MFA is required for all admin users
  return isAdmin;
};

// Session storage key for MFA verification state
const MFA_VERIFIED_KEY = 'castready_mfa_verified';
const MFA_VERIFIED_EXPIRY_KEY = 'castready_mfa_verified_expiry';
const MFA_SESSION_DURATION = 4 * 60 * 60 * 1000; // 4 hours

export const useMFASessionVerification = () => {
  const [isSessionVerified, setIsSessionVerified] = useState(false);

  useEffect(() => {
    // Check if session is already verified
    const verified = sessionStorage.getItem(MFA_VERIFIED_KEY);
    const expiry = sessionStorage.getItem(MFA_VERIFIED_EXPIRY_KEY);

    if (verified === 'true' && expiry) {
      const expiryTime = parseInt(expiry, 10);
      if (Date.now() < expiryTime) {
        setIsSessionVerified(true);
      } else {
        // Clear expired verification
        sessionStorage.removeItem(MFA_VERIFIED_KEY);
        sessionStorage.removeItem(MFA_VERIFIED_EXPIRY_KEY);
      }
    }
  }, []);

  const markSessionVerified = useCallback(() => {
    sessionStorage.setItem(MFA_VERIFIED_KEY, 'true');
    sessionStorage.setItem(MFA_VERIFIED_EXPIRY_KEY, String(Date.now() + MFA_SESSION_DURATION));
    setIsSessionVerified(true);
  }, []);

  const clearSessionVerification = useCallback(() => {
    sessionStorage.removeItem(MFA_VERIFIED_KEY);
    sessionStorage.removeItem(MFA_VERIFIED_EXPIRY_KEY);
    setIsSessionVerified(false);
  }, []);

  return {
    isSessionVerified,
    markSessionVerified,
    clearSessionVerification,
  };
};
